import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Services, Loader, Container, i18n, localizationMaps } from "@simplybenefits/sb-ui-library";
import { getSignupData, getWhitelabel, marketplaceOnlySignup, resetSignup, updateFormField, validateAffiliateLink } from '../../actions/signup';
import EmailPasswordForm from './Forms/EmailPasswordForm';
import UserDetailsForm from './Forms/UserDetails';
import Dependents from './Forms/Dependents';
import Signage from './Forms/Signage';
import { globalStyles } from '../../theme';
import NavigationContainer from "./Components/NavigationContainer";
import NavigationItem from "./Components/NavigationItem";
import * as notifier from "../../actions/notifier";
import logoFullColorMedium from "../../assets/logo-full-color-medium.png";
import { getUserLanguage, removeEnrollmentData, setUserLanguage } from "../../services/session";

class MarketplaceOnlySignup extends PureComponent {
    state = {
        loading: true,
        activeStep: 0,
        highestStepSeen: 0,
        userDetails: null,
        dependents: [],
        signage: {},
        initData: {},
        sections: [],
        saved: {
            emailPasswordForm: false,
            userDetails: false,
            dependents: false
        },
        tips: {
            passwordBreakdown: true,
            coverageBreakdown: true,
            enrolledElseWhere: true,
            dependentBreakdown: true,
            addAnotherDependent: true,
            confirmBreakdown: true
        },
        stripeStatus: this.props.match.params.stripeStatus
    };

    async componentDidMount() {
        // check if the user is opening on mobile
        if ((/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i).test(navigator.userAgent)) {
            alert('The enrollment experience is best when completed on a desktop computer.');
        }

        await this._saveInitialData();
    }

    _saveInitialData = async (reset = false) => {
        const { location } = this.props;
        const isAffiliateLink = location.pathname.includes("affiliate");
        const affiliateLinkID = this.props.match.params.affiliateLinkID;
        const userID = this.props.match.params.userID;
        let accountID = this.props.match.params.accountID;

        // try to get a stored language if the user is picking up midway through the enrolment process
        const storedLanguage = getUserLanguage();

        if (isAffiliateLink && affiliateLinkID) {
            accountID = await this.props.dispatch(validateAffiliateLink(affiliateLinkID));

            if (!accountID) {
                setTimeout(() => {
                    window.location.href = 'https://simplybenefits.ca';
                }, 2000);
            } else {
                await this.props.dispatch(getWhitelabel(accountID));

                const sections = [
                    'Create Account',
                    'Details',
                    'Dependents',
                    'Beneficiaries',
                    'Trustee',
                    'Confirmation'
                ];

                await this.setState({
                    loading: false,
                    affiliateLinkID,
                    isAffiliate: true,
                    activeStep: 0,
                    highestStepSeen: 0,
                    sections
                });
            }
        } else if (userID && accountID) {
            await this.props.dispatch(getWhitelabel(accountID));

            const data = await this.props.dispatch(getSignupData(userID, accountID));

            if (!reset) { // don't set the language again on a toggle language call
                const language = storedLanguage || data?.initData?.language || "en";
                i18n.setLocale(language);
                await this.setState({ selectedLanguage: language });
            }

            if (data.initData) {
                const sections = [
                    'Create Account',
                    'Details',
                    'Dependents',
                    'Beneficiaries',
                    'Confirmation'
                ];

                this.setState({
                    loading: false,
                    activeStep: 0,
                    highestStepSeen: 0,
                    saved: this.props.saved,
                    initData: data.initData,
                    sections,
                    ...data.savedData
                });
            }
        } else {
            this.setState({
                loading: false
            });
        }
    };

    _hideTip = (type) => {
        this.setState({
            tips: {
                ...this.state.tips,
                [type]: false
            }
        });
    };

    /**
     * Pass this method to each tab view, store the data returned from each tab
     * @param data
     * @param step
     * @param isFinish
     * @private
     */
    _stepCompletion = async (data, step, isFinish = false) => {
        if (step === 'userDetails') {
            await this.setState({
                [step]: {
                    ...this.state[step],
                    ...data
                }
            });
        } else {
            await this.setState({
                [step]: data
            });
        }

        if (isFinish) {
            this._finishSignup();
        } else {
            this._handleNext();
        }
    };

    /**
     * Once all steps are complete call this method to send the data to the API and to create user
     * @private
     */
    _finishSignup = async () => {
        // ensure all sections have been saved:
        let missingSection = null;
        const promises = Object.keys(this.state.saved).map((section) => {
            if (!this.state.saved[section]) {
                missingSection = section;
            }

            return null;
        });

        await Promise.all(promises);

        if (missingSection) {
            switch (missingSection) {
                case 'emailPasswordForm':
                    this.setState({
                        activeStep: 0,
                        highestStepSeen: 0
                    });

                    break;
                case 'userDetails':
                    this.setState({
                        activeStep: 1,
                        highestStepSeen: 1
                    });

                    break;
                case 'dependents':
                    this.setState({
                        activeStep: 2,
                        highestStepSeen: 2
                    });

                    break;
                default:
                    this.setState({
                        activeStep: 0,
                        highestStepSeen: 0
                    });
            }

            this.props.dispatch(notifier.displayError({ verbiage: 'Please ensure you have completed the entire form before continuing.' }));
            return;
        }

        const dependentPromises = this.state.dependents.map((dependent) => ({
            ...dependent,
            birthdate: Services.dates.formatOutgoingDate(dependent.birthdate),
            cohabDate: Services.dates.formatOutgoingDate(dependent.cohabDate),
            dateOfMarriage: Services.dates.formatOutgoingDate(dependent.dateOfMarriage),
            eligibilityDate: Services.dates.formatOutgoingDate(dependent.eligibilityDate),
            relationship: localizationMaps.toEnglish("relationships", dependent.relationship),
            gender: localizationMaps.toEnglish("genders", dependent.gender),
            primaryBenefits: dependent.primaryBenefits.map((benefit) => localizationMaps.toEnglish('primaryBenefits', benefit))
        }));

        const dependents = await Promise.all(dependentPromises);

        const data = {
            affiliateLinkID: this.state.affiliateLinkID,
            userDetails: {
                accountID: this.state.userDetails.accountID && this.state.userDetails.accountID.length > 0 ? this.state.userDetails.accountID : 'affiliate',
                userID: this.state.userDetails.userID && this.state.userDetails.userID.length > 0 ? this.state.userDetails.userID : 'affiliate',
                firstname: this.state.userDetails.firstname,
                lastname: this.state.userDetails.lastname,
                language: this.state.selectedLanguage || "en",
                email: this.state.userDetails.email,
                password: this.state.userDetails.password,
                addressLineOne: this.state.userDetails.addressLineOne,
                addressLineTwo: this.state.userDetails.addressLineTwo || null,
                city: this.state.userDetails.city,
                postalCode: this.state.userDetails.postalCode,
                province: localizationMaps.toEnglish("provinces", this.state.userDetails.province),
                phone: this.state.userDetails.phone,
                birthdate: this.state.userDetails.birthdate,
                maritlStat: localizationMaps.toEnglish("maritalStatuses", this.state.userDetails.maritlStat),
                gender: localizationMaps.toEnglish("genders", this.state.userDetails.gender)
            },
            dependents,
            signage: this.state.signage
        };

        this.props.dispatch(marketplaceOnlySignup(data));
    };

    _setSavedState = async (section, value) => {
        await this.setState({
            ...this.state,
            saved: {
                ...this.state.saved,
                [section]: value
            }
        });

        await this.props.dispatch(updateFormField('saved', section, value));
    };

    /**
     * Toggle the language by setting the localStorage, and then removing all stored data so that translated words need to be reentered, then setting the language in state
     *
     * @async
     * @param {string} language - The language code to set for the user interface.
     * @returns {Promise<void>} A Promise that resolves once language toggling and related actions are complete.
     */
    _toggleLanguage = async (language) => {
        i18n.setLocale(language);
        setUserLanguage(language);

        removeEnrollmentData(this.props.match.params.userID);
        await this.props.dispatch(resetSignup());

        await this._saveInitialData(true);
        await this.setState({ selectedLanguage: language });
    };

    _getStepContent = (index) => {
        if (index === 1) {
            return (
                <UserDetailsForm
                    stepCompletion={this._stepCompletion}
                    setSavedState={this._setSavedState}
                    initData={this.state.initData}
                    goBack={this._handleBack}
                    hideTip={this._hideTip}
                    tips={this.state.tips}
                    isMarketplaceOnly={ true }
                    isAffiliate={ this.state.isAffiliate }
                />
            );
        }

        if (index === 2) {
            return (
                <Dependents
                    stepCompletion={this._stepCompletion}
                    setSavedState={this._setSavedState}
                    goBack={this._handleBack}
                    initData={this.state.initData}
                    hideTip={this._hideTip}
                    tips={this.state.tips}
                    isMarketplaceOnly={ true }
                    isAffiliate={ this.state.isAffiliate }
                />
            );
        }

        if (index === 3) {
            return (
                <Signage
                    stepCompletion={this._stepCompletion}
                    saved={ this.state.saved }
                    goBack={this._handleBack}
                    hideTip={this._hideTip}
                    tips={this.state.tips}
                    isAffiliate={ this.state.isAffiliate }
                />
            );
        }

        return (
            <EmailPasswordForm
                stepCompletion={this._stepCompletion}
                setSavedState={this._setSavedState}
                goBack={this._handleBack}
                hideTip={this._hideTip}
                tips={this.state.tips}
                userLanguage={ getUserLanguage() || this?.state?.userDetails?.language || 'en' }
                toggleLanguage={ this._toggleLanguage }
                isAffiliate={ this.state.isAffiliate }
            />
        );
    };

    _handleNext = () => {
        this.setState((state) => ({
            activeStep: state.activeStep + 1,
            highestStepSeen: state.activeStep + 1
        }));
    };

    _handleBack = () => {
        this.setState((state) => ({
            activeStep: state.activeStep - 1
        }));
    };

    _selectSection = (index) => {
        if (index > this.state.highestStepSeen) {
            return;
        }

        this.setState({
            highestStepSeen: index > this.state.highestStepSeen ? index : this.state.highestStepSeen,
            activeStep: index
        });
    };

    render() {
        const isAffiliate = this.state.isAffiliate;

        if (this.state.loading || (!isAffiliate && Object.keys(this.state.initData).length === 0)) {
            return null;
        }

        const { whitelabel } = this.props;
        const { sections } = this.state;

        const styles = {
            content: {
                backgroundColor: '#FFF',
                boxShadow: "0 1px 2px rgba(0,0,0,.15)",
                width: 800,
                margin: '0 auto',
                padding: '40px 0'
            },
            logo: globalStyles.logo,
            formBody: {
                width: 720,
                margin: '0 auto 30px auto'
            }
        };

        return (
            <Container background={"true"}>
                <div style={{ paddingTop: 20 }}>
                    <img
                        alt={ `${ whitelabel.name || 'Simply Benefits' } Logo` }
                        src={ whitelabel.logo || logoFullColorMedium }
                        style={styles.logo}
                    />
                </div>

                <div style={ styles.content }>
                    <NavigationContainer>
                        <NavigationItem
                            label="Create Account"
                            isSeen={ this.state.highestStepSeen >= sections.indexOf('Create Account')}
                            isActive={ this.state.activeStep === sections.indexOf('Create Account') }
                            onClick={ () => this._selectSection(sections.indexOf('Create Account')) }
                            customStyle={{ flex: 1.5 }}
                        />

                        <NavigationItem
                            label="Details"
                            isSeen={ this.state.highestStepSeen >= sections.indexOf('Details')}
                            isActive={ this.state.activeStep === sections.indexOf('Details') }
                            onClick={ () => this._selectSection(sections.indexOf('Details')) }
                            customStyle={{ flex: 0.8 }}
                        />

                        <NavigationItem
                            label="Dependents"
                            isSeen={ this.state.highestStepSeen >= sections.indexOf('Dependents')}
                            isActive={ this.state.activeStep === sections.indexOf('Dependents') }
                            onClick={ () => this._selectSection(sections.indexOf('Dependents')) }
                            customStyle={{ flex: 1 }}
                        />

                        <NavigationItem
                            label="Confirmation"
                            isSeen={ this.state.highestStepSeen >= sections.indexOf('Confirmation')}
                            isActive={ this.state.activeStep === sections.indexOf('Confirmation') }
                            onClick={ () => this._selectSection(sections.indexOf('Confirmation')) }
                            customStyle={{ flex: 1 }}
                        />
                    </NavigationContainer>

                    <div style={ styles.formBody }>
                        { this._getStepContent(this.state.activeStep) }
                    </div>
                </div>

                {this.props.loader?.isLoading && <Loader whitelabel={ whitelabel } noGif />}

                <span id="testID" />
            </Container>
        );
    }
}

MarketplaceOnlySignup.propTypes = {
    loading: PropTypes.bool,
    whitelabel: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    whitelabel: state.whitelabel,
    saved: state.signup.saved
});

export default (connect(mapStateToProps)(MarketplaceOnlySignup));